<template>
  <div class="login-page" :style="{ backgroundImage: `url(${homeBg})` }">
    <div class="page-veil"></div>
    <div class="login-shell">
      <header class="brand-block">
        <img class="logo" :src="logoImg" alt="YES" />
        <p class="brand-sub">口语测评 · 班级活动</p>
      </header>

      <div class="login-panel">
        <section v-if="loading" class="state-text">
          <span class="spinner"></span>
          <p>正在载入活动…</p>
        </section>

        <section v-else-if="loadError" class="state-text">
          <p class="state-error">{{ loadError }}</p>
        </section>

        <form v-else class="login-form" @submit.prevent="handleSubmit">
          <div class="field readonly">
            <span class="field-label">学校 / 机构</span>
            <p class="field-value">{{ activity.schoolName }}</p>
          </div>

          <div class="field readonly">
            <span class="field-label">班级</span>
            <p class="field-value">{{ activity.className }}</p>
          </div>

          <label class="field">
            <span class="field-label">学生名</span>
            <div class="control-wrap">
              <select v-model="studentId" :disabled="!activity.published">
                <option value="">请选择学生</option>
                <option
                  v-for="student in activity.students"
                  :key="student.id"
                  :value="student.id"
                >
                  {{ student.name }}
                </option>
              </select>
            </div>
          </label>

          <label class="field">
            <span class="field-label">激活码</span>
            <input
              v-model="serialCode"
              type="text"
              maxlength="18"
              inputmode="numeric"
              placeholder="请输入 18 位序列号"
              :disabled="!activity.published"
            />
          </label>

          <p v-if="submitError" class="error">{{ submitError }}</p>

          <button
            class="submit-btn primary"
            type="submit"
            :disabled="submitting || !activity.published"
          >
            {{ submitting ? "提交中…" : "进入学习" }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { getPublicActivity, loginActivity } from '@/api/activity'
import { ensureStudentSession, setStudentSession } from '@/utils/auth'
import { resolveActivityId } from '@/utils/activity-route'
import homeBg from '@/assets/home-bg.jpg'
import logoImg from '@/assets/logo.png'

export default {
  name: 'ActivityLogin',
  data() {
    return {
      homeBg,
      logoImg,
      activityId: '',
      loading: true,
      loadError: '',
      submitting: false,
      submitError: '',
      studentId: '',
      serialCode: '',
      activity: {
        schoolName: '',
        className: '',
        published: false,
        students: []
      }
    }
  },
  created() {
    this.activityId = resolveActivityId(this.$route)
    if (!this.activityId) {
      this.loading = false
      this.loadError = '请使用活动链接进入'
      return
    }
    if (this.$route.path === '/' || !this.$route.params.activityId) {
      this.$router.replace(`/a/${this.activityId}`)
    }
    const session = ensureStudentSession(this.activityId)
    if (session) {
      this.$router.replace(`/a/${this.activityId}/lessons`)
      return
    }
    this.fetchActivity()
  },
  methods: {
    async fetchActivity() {
      this.loading = true
      this.loadError = ''
      try {
        const res = await getPublicActivity(this.activityId)
        this.activity = res.data
        if (!this.activity.published) {
          this.loadError = '活动未上架'
        }
      } catch (error) {
        this.loadError = error.message || '活动不存在'
      } finally {
        this.loading = false
      }
    },
    async handleSubmit() {
      this.submitError = ''
      if (!this.studentId) {
        this.submitError = '请选择学生'
        return
      }
      if (!this.serialCode.trim()) {
        this.submitError = '请输入序列号'
        return
      }
      this.submitting = true
      try {
        const res = await loginActivity(this.activityId, {
          studentId: this.studentId,
          serialCode: this.serialCode.trim()
        })
        setStudentSession(res.data)
        this.$router.replace(`/a/${this.activityId}/lessons`)
      } catch (error) {
        this.submitError = error.message || '登录失败'
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.login-page {
  --ink: #1c2a1a;
  --muted: #5f6d58;
  --line: rgba(28, 42, 26, 0.12);
  --panel: rgba(255, 252, 245, 0.92);
  --accent: #1aa39a;
  --accent-deep: #12867f;
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px 18px 40px;
  background-color: #dce8a8;
  background-size: cover;
  background-position: center;
  overflow: hidden;
}

.page-veil {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(232, 240, 193, 0.35) 0%, rgba(255, 255, 255, 0.28) 45%, rgba(220, 232, 168, 0.55) 100%);
  pointer-events: none;
}

.login-shell {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 400px;
  animation: rise-in 0.55s ease both;
}

.brand-block {
  text-align: center;
  margin-bottom: 22px;
}

.logo {
  display: block;
  width: min(72vw, 220px);
  height: auto;
  margin: 0 auto;
  filter: drop-shadow(0 10px 20px rgba(28, 42, 26, 0.14));
}

.brand-sub {
  margin: 10px 0 0;
  font-size: 13px;
  letter-spacing: 0.08em;
  color: var(--muted);
}

.login-panel {
  background: var(--panel);
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 24px;
  padding: 28px 22px 26px;
  box-shadow:
    0 18px 40px rgba(28, 42, 26, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
}

.login-form,
.state-text {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.state-text {
  align-items: center;
  justify-content: center;
  min-height: 180px;
  text-align: center;
  color: var(--muted);
}

.state-error {
  color: #b42318;
  font-size: 15px;
}

.spinner {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(26, 163, 154, 0.2);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--muted);
}

.field-value {
  margin: 0;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(232, 240, 193, 0.45);
  border: 1px solid var(--line);
  color: var(--ink);
  font-size: 15px;
  font-weight: 600;
  line-height: 1.45;
}

.control-wrap,
.field input,
.field select {
  width: 100%;
}

.field select,
.field input {
  height: 48px;
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 0 14px;
  font-size: 15px;
  color: var(--ink);
  background: #fff;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  -webkit-appearance: none;
  appearance: none;
}

.field select {
  background-image:
    linear-gradient(45deg, transparent 50%, #6d7a66 50%),
    linear-gradient(135deg, #6d7a66 50%, transparent 50%);
  background-position:
    calc(100% - 18px) 21px,
    calc(100% - 12px) 21px;
  background-size: 6px 6px, 6px 6px;
  background-repeat: no-repeat;
  padding-right: 36px;
}

.field select:focus,
.field input:focus {
  border-color: rgba(26, 163, 154, 0.55);
  box-shadow: 0 0 0 4px rgba(26, 163, 154, 0.12);
}

.submit-btn {
  margin-top: 6px;
  height: 50px;
  border: none;
  border-radius: 999px;
  background: #fff;
  color: var(--ink);
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(28, 42, 26, 0.08);
  transition: transform 0.15s ease, opacity 0.15s ease, background 0.2s ease;
}

.submit-btn.primary {
  background: linear-gradient(180deg, #24b7ad 0%, var(--accent-deep) 100%);
  color: #fff;
  box-shadow: 0 12px 24px rgba(18, 134, 127, 0.28);
}

.submit-btn:active {
  transform: scale(0.98);
}

.submit-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.error {
  margin: 0;
  text-align: center;
  color: #b42318;
  font-size: 13px;
}

@keyframes rise-in {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>



// WEBPACK FOOTER //
// src/views/ActivityLogin.vue